// Card copy stays short on purpose: `features` holds the few lines that make
// someone pick this tier, and `includes` carries everything from the tier
// below. Full limits live in PLAN_COMPARISON. Every line here must match
// PLAN_LIMITS in server/changedetectionio/model/App.py and the plan gates
// (plans-match-server.test.ts checks the numbers).

// The figures marketing copy may quote. Blog posts, docs and the AI chat prompt
// read these instead of hard-coding a price that then goes stale.
export const PRICING_FACTS = {
  free: { price: 0, pages: 5, checks: 150, fastest: "every hour" },
  starter: {
    price: 9,
    priceYearly: 86,
    pages: 50,
    checks: 30000,
    fastest: "every 10 minutes",
  },
  pro: {
    price: 25,
    priceYearly: 240,
    pages: 250,
    checks: 150000,
    fastest: "every 5 minutes",
  },
  business: {
    label: "Agency",
    price: 149,
    priceYearly: 1430,
    pages: 1000,
    checks: 750000,
    fastest: "every 5 minutes",
    seats: 5,
  },
  weights: { plain: 1, browser: 2, antibot: 5 },
  trialDays: 5,
  yearlyDiscountPercent: 20,
} as const;

export type BillingPeriod = "monthly" | "yearly";

/** Yearly price for a paid plan id, or null for Free */
export function yearlyPriceFor(planId: string): number | null {
  const facts = (
    PRICING_FACTS as unknown as Record<string, { priceYearly?: number }>
  )[planId];
  return facts?.priceYearly ?? null;
}

// One sentence that explains the meter wherever a price is shown
export const CHECK_COUNTING_NOTE =
  "A page checked every hour uses about 720 checks a month. A page that needs a browser counts 2 per check, one behind an anti-bot wall 5, and retries after an error are free.";

export const PLANS = [
  {
    id: "free",
    label: "Free",
    price: "€0",
    period: "forever",
    color: "#6b7280",
    subtitle: "For the few pages you keep refreshing",
    includes: null,
    features: [
      "5 pages, 150 checks a month",
      "Each page checked once a day",
      "Alerts by email, Telegram or browser push",
      "Chrome and Firefox extension",
      "API, MCP and RSS feeds",
    ],
    cta: "Start free",
    ctaHref: "/login" as const,
    ctaSearch: { mode: "signup" } as { mode: string },
    highlighted: false,
    beta: false,
  },
  {
    id: "starter",
    label: "Starter",
    price: "€9",
    period: "/ month",
    color: "#0891b2",
    subtitle: "For the sites you check every day",
    includes: "Everything in Free, plus",
    features: [
      "50 pages, 30,000 checks a month",
      "Checked as often as every 10 minutes",
      "90 days of history",
      "Import all your URLs at once",
    ],
    cta: "Get Starter",
    ctaHref: "/dashboard/billing" as const,
    ctaSearch: { plan: "starter" } as { plan: string },
    highlighted: false,
    beta: false,
  },
  {
    id: "pro",
    label: "Pro",
    price: "€25",
    period: "/ month",
    color: "#7c3aed",
    subtitle: "For prices, stock and slots where an hour is too late",
    includes: "Everything in Starter, plus",
    features: [
      "250 pages, 150,000 checks a month",
      "Checked as often as every 5 minutes",
      "AI summaries of what changed",
      "Screenshots and visual diffs",
      "1 year of history",
    ],
    cta: "Get Pro",
    ctaHref: "/dashboard/billing" as const,
    ctaSearch: { plan: "pro" } as { plan: string },
    highlighted: true,
    beta: false,
  },
  {
    id: "business",
    label: "Agency",
    price: "€149",
    period: "/ month",
    color: "#d97706",
    subtitle: "For teams that watch pages for clients",
    includes: "Everything in Pro, plus",
    features: [
      "1,000 pages, 750,000 checks a month",
      "5 team seats sharing one allowance",
      "50,000 API requests a day",
    ],
    cta: "Get Agency",
    ctaHref: "/dashboard/billing" as const,
    ctaSearch: { plan: "business" } as { plan: string },
    highlighted: false,
    beta: false,
  },
] as const;

type ComparisonValue = string | boolean;

export const PLAN_COMPARISON: ReadonlyArray<{
  label: string;
  values: readonly [
    ComparisonValue,
    ComparisonValue,
    ComparisonValue,
    ComparisonValue,
  ];
}> = [
  { label: "Pages you can watch", values: ["5", "50", "250", "1,000"] },
  {
    label: "Checks a month",
    values: ["150", "30,000", "150,000", "750,000"],
  },
  {
    label: "Fastest checks",
    values: ["Every hour", "Every 10 min", "Every 5 min", "Every 5 min"],
  },
  { label: "History kept", values: ["30 days", "90 days", "1 year", "1 year"] },
  { label: "Snapshots per page", values: ["8", "25", "100", "500"] },
  { label: "Manual rechecks a day", values: ["3", "10", "25", "100"] },
  { label: "API requests a day", values: ["100", "1,000", "10,000", "50,000"] },
  {
    label: "Email, Telegram and push alerts",
    values: [true, true, true, true],
  },
  { label: "90+ webhook integrations", values: [true, true, true, true] },
  { label: "Browser extension, MCP and RSS", values: [true, true, true, true] },
  { label: "Bulk import", values: [false, true, true, true] },
  { label: "AI change summaries", values: [false, false, true, true] },
  { label: "Screenshots and visual diffs", values: [false, false, true, true] },
  { label: "Team seats", values: [false, false, false, "5"] },
];

export const PLAN_LABELS: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  business: "Agency",
  pro_trial: "Free (Pro features)",
  pro_legacy: "Pro (legacy)",
};

export const PLAN_COLORS: Record<string, string> = {
  ...Object.fromEntries(PLANS.map((p) => [p.id, p.color])),
  pro_trial: "#a855f7",
  pro_legacy: "#7c3aed",
};

/** The price line a plan card shows for the chosen billing period */
export function displayPrice(
  plan: { id: string; price: string; period: string },
  billing: BillingPeriod,
): { price: string; period: string; note: string | null } {
  const yearly = billing === "yearly" ? yearlyPriceFor(plan.id) : null;
  if (yearly === null)
    return { price: plan.price, period: plan.period, note: null };
  const perMonth = (yearly / 12).toFixed(2).replace(/\.00$/, "");
  return {
    price: `€${yearly.toLocaleString("en-US")}`,
    period: "/ year",
    note: `€${perMonth} a month, billed yearly`,
  };
}
